/**
 * BusinessContextCard — the AI-sharpened business-context brief, up front on the project Overview
 * (Release 2, T-35; demo L629–647: business value, pain points, customer concerns).
 *
 * States: loading → error (Retry) → empty (Generate) → brief (Regenerate / Edit) → editing (Save).
 * The brief never carries deal size or financial figures: the server post-filters generated output
 * and rejects a manual edit that contains them (400 FINANCIAL_DETAILS_NOT_ALLOWED, shown inline).
 *
 * Regenerating over a hand-edited brief asks for confirmation first (and again if the server answers
 * 409 BRIEF_EDITED because an edit landed meanwhile) — a regenerate replaces the edit wholesale.
 *
 * Rendering reuses `markdownToHtml` (lib/markdown.ts), which HTML-escapes its input before
 * formatting — the same renderer the artifact preview uses.
 */
import { useId, useState } from "react";
import { FileText, Loader2, Pencil, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
  useBusinessContext,
  useGenerateBusinessContext,
  useSaveBusinessContext,
} from "@/hooks/useBusinessContext";
import { useProject } from "@/hooks/useProjects";
import { apiErrorCode, apiErrorMessage } from "@/lib/api";
import { canActAsMember } from "@/lib/project-permissions";
import type { Project } from "@/types";
import { markdownToHtml } from "@/lib/markdown";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";

/** Generate / edit roles. The server also requires project membership (leadership/admin bypass). */
const WRITE_ROLES = ["pm", "sa", "leadership", "admin"];
/** DB CHECK (V056) and server validation. */
export const BUSINESS_CONTEXT_MAX_CHARS = 20_000;

function fmtDateTime(value: string | null): string {
  if (!value) return "";
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
}

export interface BusinessContextCardProps {
  projectId: string;
}

export function BusinessContextCard({ projectId }: BusinessContextCardProps): JSX.Element {
  const { user } = useAuth();
  const { data: project } = useProject(projectId) as { data: Project | undefined };
  // Reads are open to every role; writes are member-only (404 to non-members). False while loading.
  const canWrite = canActAsMember(user?.role, WRITE_ROLES, project);
  const query = useBusinessContext(projectId);
  const generate = useGenerateBusinessContext(projectId);
  const save = useSaveBusinessContext(projectId);

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const headingId = useId();
  const textareaId = useId();
  const saveErrorId = useId();

  const brief = query.data ?? null;

  const runGenerate = (overwriteEdits: boolean): void => {
    setConfirmOverwrite(false);
    generate.mutate(
      { overwriteEdits },
      {
        onSuccess: (data) => {
          toast.success(
            data.redacted_lines > 0
              ? "Business context generated (financial details removed)"
              : "Business context generated",
          );
        },
        onError: (err) => {
          if (apiErrorCode(err) === "BRIEF_EDITED") {
            setConfirmOverwrite(true);
            return;
          }
          toast.error(apiErrorMessage(err, "Could not generate the business context. Please try again."));
        },
      },
    );
  };

  const handleGenerateClick = (): void => {
    if (brief?.source === "edited") setConfirmOverwrite(true);
    else runGenerate(false);
  };

  const startEditing = (): void => {
    setDraft(brief?.content_md ?? "");
    setSaveError(null);
    setEditing(true);
  };

  const handleSave = (): void => {
    setSaveError(null);
    save.mutate(draft, {
      onSuccess: () => {
        setEditing(false);
        toast.success("Business context saved");
      },
      onError: (err) => {
        if (apiErrorCode(err) === "FINANCIAL_DETAILS_NOT_ALLOWED") {
          const lines = (err as { response?: { data?: { details?: { lines?: number[] } } } }).response?.data
            ?.details?.lines;
          setSaveError(
            `Remove deal size, prices and other financial figures${lines?.length ? ` (line ${lines.join(", ")})` : ""}, then save again.`,
          );
          return;
        }
        setSaveError(apiErrorMessage(err, "Could not save the business context. Please try again."));
      },
    });
  };

  const trimmedDraft = draft.trim();
  const draftTooLong = draft.length > BUSINESS_CONTEXT_MAX_CHARS;

  let body: JSX.Element;
  if (query.isLoading) {
    body = (
      <div className="space-y-2" role="status" aria-label="Loading business context">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
      </div>
    );
  } else if (query.isError) {
    body = (
      <div className="flex flex-col items-start gap-3" role="alert">
        <p className="text-sm text-error">Could not load the business context.</p>
        <Button variant="outline" size="sm" onClick={() => void query.refetch()}>
          Retry
        </Button>
      </div>
    );
  } else if (editing) {
    body = (
      <div className="space-y-3">
        <label htmlFor={textareaId} className="text-sm font-medium text-text-primary">
          Business context (markdown)
        </label>
        <Textarea
          id={textareaId}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={14}
          className="font-mono text-[13px]"
          aria-invalid={Boolean(saveError) || draftTooLong}
          aria-describedby={saveError ? saveErrorId : undefined}
        />
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className={draftTooLong ? "text-xs text-error" : "text-xs text-text-secondary"}>
            {draft.length.toLocaleString()} / {BUSINESS_CONTEXT_MAX_CHARS.toLocaleString()} characters
            {" · "}Do not include deal size or financial figures.
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={save.isPending}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={save.isPending || !trimmedDraft || draftTooLong}>
              {save.isPending && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
              Save
            </Button>
          </div>
        </div>
        {saveError && (
          <p id={saveErrorId} className="text-sm text-error" role="alert">
            {saveError}
          </p>
        )}
      </div>
    );
  } else if (!brief) {
    body = (
      <div className="flex flex-col items-start gap-3">
        <p className="text-sm text-text-secondary">
          No business context yet. Generate a short brief — business value, pain points, customer concerns —
          from the presales material (Oscar intake and the SOW). Deal size and financial figures are never
          included.
        </p>
        {canWrite && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => runGenerate(false)} disabled={generate.isPending}>
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Sparkles className="h-4 w-4" aria-hidden="true" />
              )}
              {generate.isPending ? "Generating…" : "Generate"}
            </Button>
            <Button variant="outline" size="sm" onClick={startEditing} disabled={generate.isPending}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Write manually
            </Button>
          </div>
        )}
      </div>
    );
  } else {
    body = (
      <div className="space-y-3">
        <div
          className="prose-sm text-sm text-text-primary"
          aria-label="Business context brief"
          // Server content, HTML-escaped by markdownToHtml before formatting (see lib/markdown.ts).
          dangerouslySetInnerHTML={{ __html: markdownToHtml(brief.content_md) }}
        />
        <p className="text-xs text-text-secondary">
          {brief.source === "edited"
            ? `Edited ${fmtDateTime(brief.edited_at)}${brief.edited_by ? ` by ${brief.edited_by}` : ""}`
            : `Generated ${fmtDateTime(brief.generated_at)}${brief.generated_by ? ` by ${brief.generated_by}` : ""}`}
          {brief.source === "edited" && brief.generated_at ? ` · first generated ${fmtDateTime(brief.generated_at)}` : ""}
        </p>
      </div>
    );
  }

  const showHeaderActions = canWrite && brief && !editing && !query.isLoading && !query.isError;

  return (
    <Card aria-labelledby={headingId}>
      <CardHeader className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-primary" aria-hidden="true" />
          <h2 id={headingId} className="text-base font-semibold text-text-primary">
            Business Context
          </h2>
          {brief && (
            <Badge variant={brief.source === "edited" ? "neutral" : "info"}>
              {brief.source === "edited" ? "Edited" : "AI generated"}
            </Badge>
          )}
        </div>
        {showHeaderActions && (
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleGenerateClick} disabled={generate.isPending}>
              {generate.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
              )}
              {generate.isPending ? "Regenerating…" : "Regenerate"}
            </Button>
            <Button variant="outline" size="sm" onClick={startEditing} disabled={generate.isPending}>
              <Pencil className="h-4 w-4" aria-hidden="true" />
              Edit
            </Button>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {/* Announce generation progress to assistive tech — it can take ~20 s. */}
        <p className="sr-only" role="status" aria-live="polite">
          {generate.isPending ? "Generating business context…" : ""}
        </p>
        {body}
      </CardContent>

      <Dialog open={confirmOverwrite} onOpenChange={(open) => !open && setConfirmOverwrite(false)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Replace the edited brief?</DialogTitle>
            <DialogDescription>
              This brief was edited by hand. Regenerating replaces those edits with a new AI-generated brief.
              This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOverwrite(false)}>
              Keep edits
            </Button>
            <Button variant="danger" onClick={() => runGenerate(true)}>
              Replace with new brief
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default BusinessContextCard;
